import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Download, Share2, BookOpen } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const TVASimulator = () => {
  const [inputs, setInputs] = useState({
    caHT: '80000',
    achatsHT: '20000',
    tauxTVA: '20',
    tauxTVAAchats: '20'
  });
  const [results, setResults] = useState({});

  const calculate = () => {
    const caHT = parseFloat(inputs.caHT) || 0;
    const achatsHT = parseFloat(inputs.achatsHT) || 0;
    const tauxVente = parseFloat(inputs.tauxTVA) || 20;
    const tauxAchat = parseFloat(inputs.tauxTVAAchats) || 20;

    const tvaCollectee = caHT * (tauxVente / 100);
    const tvaDeductible = achatsHT * (tauxAchat / 100);
    const tvaNette = tvaCollectee - tvaDeductible;
    const caTTC = caHT + tvaCollectee;

    setResults({
      caHT, caTTC: Math.round(caTTC),
      tvaCollectee: Math.round(tvaCollectee),
      tvaDeductible: Math.round(tvaDeductible),
      tvaNette: Math.round(tvaNette),
      credit: tvaNette < 0,
      montantAbsolu: Math.abs(Math.round(tvaNette))
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Simulateur TVA - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - TVA à payer / récupérer', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`CA HT : ${parseInt(inputs.caHT).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Achats HT : ${parseInt(inputs.achatsHT).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`TVA collectée : ${results.tvaCollectee?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`TVA déductible : ${results.tvaDeductible?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`TVA nette : ${results.tvaNette?.toLocaleString()} EUR (${results.credit ? 'Crédit' : 'À payer'})`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : CGI art. 256 et suivants, service-public.fr TVA 2026', 15, y);
    doc.save('simulation-tva-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Paramètres TVA
            </CardTitle>
            <CardDescription>Calculez votre TVA nette à payer ou à récupérer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Chiffre d'affaires HT (€)</Label>
              <Input data-testid="ca-input" type="text" inputMode="numeric" value={inputs.caHT}
                onChange={(e) => handleInputChange('caHT', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="80000" className="mt-1" />
            </div>
            <div>
              <Label>Achats / dépenses HT (€)</Label>
              <Input data-testid="achats-input" type="text" inputMode="numeric" value={inputs.achatsHT}
                onChange={(e) => handleInputChange('achatsHT', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="20000" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Taux TVA ventes (%)</Label>
                <div className="grid grid-cols-3 gap-1 mt-2">
                  {['5.5', '10', '20'].map(t => (
                    <Button key={t} size="sm" data-testid={`tva-vente-${t}-btn`}
                      variant={inputs.tauxTVA === t ? 'default' : 'outline'}
                      className={inputs.tauxTVA === t ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      onClick={() => handleInputChange('tauxTVA', t)}>{t}%</Button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Taux TVA achats (%)</Label>
                <div className="grid grid-cols-3 gap-1 mt-2">
                  {['5.5', '10', '20'].map(t => (
                    <Button key={t} size="sm" data-testid={`tva-achat-${t}-btn`}
                      variant={inputs.tauxTVAAchats === t ? 'default' : 'outline'}
                      className={inputs.tauxTVAAchats === t ? 'bg-blue-600 hover:bg-blue-700' : ''}
                      onClick={() => handleInputChange('tauxTVAAchats', t)}>{t}%</Button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>CGI art. 256 à 298 - Régime de la TVA</li>
                    <li>Taux normal 20%, intermédiaire 10%, réduit 5.5%</li>
                    <li>Franchise en base : 25 000€ (services) / 85 000€ (ventes) depuis 2026</li>
                    <li>service-public.fr - entreprendre.service-public.fr</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" /> Résultats TVA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600 mb-1">CA HT</p>
                <p className="text-xl font-bold text-blue-900">{results.caHT?.toLocaleString()}€</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600 mb-1">CA TTC</p>
                <p className="text-xl font-bold text-blue-900">{results.caTTC?.toLocaleString()}€</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between p-3 bg-purple-50 rounded">
                <span className="text-sm text-purple-700">TVA collectée (ventes)</span>
                <span className="text-sm font-bold text-purple-900">+{results.tvaCollectee?.toLocaleString()}€</span>
              </div>
              <div className="flex justify-between p-3 bg-green-50 rounded">
                <span className="text-sm text-green-700">TVA déductible (achats)</span>
                <span className="text-sm font-bold text-green-900">-{results.tvaDeductible?.toLocaleString()}€</span>
              </div>
            </div>

            <div className={`p-6 rounded-lg border-2 text-center ${results.credit ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
              <p className="text-sm text-gray-600 mb-1">{results.credit ? 'Crédit de TVA (à récupérer)' : 'TVA nette à payer'}</p>
              <p className={`text-3xl font-black ${results.credit ? 'text-green-900' : 'text-red-900'}`} data-testid="result-tva-nette">
                {results.credit ? '+' : '-'}{results.montantAbsolu?.toLocaleString()}€
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {results.credit ? 'Vous pouvez demander un remboursement ou l\'imputer sur la prochaine déclaration' : 'Montant à reverser à l\'État'}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : CGI art. 256-298, service-public.fr, impots.gouv.fr</p>
              <p>Barèmes TVA 2026 | Franchise en base réformée 2026</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TVASimulator;
